import { mapValues } from 'lodash-es';
import { z } from 'zod';

import { getStructuralPath } from '@fragment/path-utils/path';
import { SafeStringSchema } from '@fragment/types';
import {
  currencyCodes,
  type CurrencyCodeSchema,
} from '@fragment/types/currencyCodes';
import {
  BaseChartOfAccounts,
  type BaseSchemaLedgerAccountInputType,
  ConsistencyConfigSchema,
  PaymentAccountingEventKeySchema,
  PaymentTypeDirectionSchema,
  PostLinesAsSchema,
  SchemaEntryGroup,
  SchemaPaymentTypeStatusSchema,
  SystemLineKindSchema,
} from '@fragment/types/schemas';
import {
  ParameterizedAmount,
  ParameterizedString,
} from '@fragment/types/strings';
import { SchemaTxMatchInput } from '@fragment/types/tx';

import { fillParams, getSchemaObjectParameters } from './parameterization';
import { transformAndValidateCoA } from './utils/transformers/coa';
import { transformAndValidateEntries } from './utils/transformers/entries';
import { validateGroup } from './utils/transformers/groups';
import {
  transformAndValidatePayments,
  validateReservedPaymentEntryTypes,
} from './utils/transformers/payments';

export const SchemaCurrencyMatchInput = z
  .object({
    customCurrencyId: ParameterizedString.optional(),
    code: ParameterizedString.refine(
      (str) => {
        const params = getSchemaObjectParameters(str);
        if (params.length === 0) {
          return currencyCodes.includes(
            str as z.TypeOf<typeof CurrencyCodeSchema>
          );
        }
        return params.length === 1 && str === `{{${params[0]}}}`;
      },
      (str) => ({ message: `Invalid currency code: ${str}` })
    ),
  })
  .refine(({ code, customCurrencyId }) => {
    const codeIsParameterized = getSchemaObjectParameters(code).length > 0;
    const customCurrencyIdIsParameterized =
      !!customCurrencyId &&
      getSchemaObjectParameters(customCurrencyId).length > 0;
    if (codeIsParameterized) {
      // When code is parameterized, customCurrencyId (if provided) must also be
      // parameterized. A literal customCurrencyId only makes sense with code 'CUSTOM'.
      return !customCurrencyId || customCurrencyIdIsParameterized;
    }
    if (code === 'CUSTOM') {
      // Literal CUSTOM requires customCurrencyId (parameterized or literal).
      return !!customCurrencyId;
    }
    // Literal non-CUSTOM code cannot have customCurrencyId.
    return !customCurrencyId;
  });

export type SchemaLedgerAccountInputType = BaseSchemaLedgerAccountInputType & {
  // Override children so that they are the same type recursively
  children?: SchemaLedgerAccountInputType[];
  parameters?: string[];
  parent?: {
    path: string;
    ledger: {
      id: string;
    };
  };
};

/**
 * Partially fill in a SchemaLedgerAccountInputType with the entry parameters.
 */
export const reparameterizeSchemaLedgerAccountInput = <
  T extends SchemaLedgerAccountInputType
>({
  account,
  entryParameters,
  fillParamOptions = {},
}: {
  account: T & { path: string };
  entryParameters: Record<string, string> | undefined;
  fillParamOptions?: Parameters<typeof fillParams>[2];
}): T & { path: string } => {
  if (!entryParameters) {
    return account;
  }
  const filledAccount = fillParams(account, entryParameters, {
    allowMissing: true,
    ...fillParamOptions,
  });
  return {
    ...filledAccount,
    parameters: account.parameters?.filter((param) =>
      Object.prototype.hasOwnProperty.call(entryParameters, param)
    ),
  };
};

export const ChartOfAccounts = BaseChartOfAccounts.transform((coa, ctx) => {
  const { filledIn, errors } = transformAndValidateCoA({ coa });
  errors.forEach(({ message, path }) =>
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path,
    })
  );
  return filledIn;
}).transform((coa) => {
  const accountPathToAccount: Map<
    string,
    SchemaLedgerAccountInputType & { path: string }
  > = new Map();
  const walk = (account: SchemaLedgerAccountInputType, path?: string) => {
    const newPath = path ? `${path}/${account.key}` : account.key;
    accountPathToAccount.set(newPath, { ...account, path: newPath });
    account.children?.forEach((child) => walk(child, newPath));
  };
  coa.accounts.forEach((account) => walk(account));
  return {
    ...coa,
    accountPathToAccount,
  };
});

export type ChartOfAccountsType = z.infer<typeof ChartOfAccounts>;

const SchemaLedgerLineTagInput = z.object({
  key: SafeStringSchema.refine((key) => key.trim().length >= 1, {
    message: 'Tag key cannot be blank',
  }).refine((key) => key.length <= 128, {
    message: 'Tag key cannot be longer than 128 characters',
  }),
  value: ParameterizedString,
});

export const SchemaLedgerLineInput = z.object({
  account: z.object({
    path: ParameterizedString,
  }),
  amount: ParameterizedAmount().optional(),
  key: SafeStringSchema,
  currency: z.optional(SchemaCurrencyMatchInput),
  description: ParameterizedString.optional(),
  tx: SchemaTxMatchInput.optional(),
  tags: z.array(SchemaLedgerLineTagInput).optional(),
  repeated: z
    .object({
      key: SafeStringSchema.refine((k) => k.trim().length >= 1, {
        message: 'repeated.key cannot be empty',
      }),
    })
    .optional(),
});

type SchemaLedgerLineInputType = z.infer<typeof SchemaLedgerLineInput>;

type ParameterizedSchemaLedgerLineInputType<
  T extends Pick<
    SchemaLedgerLineInputType,
    'account' | 'amount' | 'description' | 'currency' | 'tx'
  >
> = T & {
  amountParameters: string[];
  parameters: string[];
};

/**
 * This function converts a schema ledger line input into a parameterized
 * version, filling in any parameters that are available in the entry first.
 */
export const parameterizeSchemaLedgerLineInput = <
  T extends Pick<
    SchemaLedgerLineInputType,
    'account' | 'amount' | 'description' | 'currency' | 'tx'
  >
>({
  line,
  entryParameters,
}: {
  line: T;
  entryParameters: Record<string, string> | undefined;
}): ParameterizedSchemaLedgerLineInputType<T> => {
  const fullLine = { ...line } as ParameterizedSchemaLedgerLineInputType<T>;

  if (entryParameters) {
    const filledLine = fillParams(
      {
        account: line.account,
        amount: line.amount,
        description: line.description,
        currency: line.currency,
        tx: line.tx,
        // Tags have parameterized values — fill them in with entry parameters.
        // The `tags` field may be present on the line depending on the caller's type.
        ...('tags' in line && line.tags ? { tags: line.tags } : {}),
      },
      entryParameters,
      { allowMissing: true, allowMissingInChildren: true }
    );

    Object.assign(fullLine, filledLine);
  }
  Object.assign(fullLine, {
    amountParameters: getSchemaObjectParameters(fullLine.amount),
    parameters: getSchemaObjectParameters(fullLine),
  });
  return fullLine;
};

/**
 * Adds to each line: `accountPath`, the `account` its path resolves to,
 * `currency` (the line's, else the account's), `amountParameters`, and
 * `parameters` — the line's plus the account's.
 */
const enrichSchemaLines = <
  T extends Pick<
    SchemaLedgerLineInputType,
    'account' | 'amount' | 'description' | 'currency' | 'tx'
  >
>({
  lines,
  entryParameters,
  accountPathToAccount,
}: {
  lines: T[];
  entryParameters: Record<string, string> | undefined;
  accountPathToAccount: ChartOfAccountsType['accountPathToAccount'];
}) =>
  lines.map((line) => {
    const parameterizedLine = parameterizeSchemaLedgerLineInput({
      line,
      entryParameters,
    });
    const accountPath = parameterizedLine.account.path;
    // Strip out template values. e.g. a/b:c -> a/b
    const structuralPath = getStructuralPath(accountPath);

    // TODO: Fill in params on the account that are available in the
    // entry. This shouldn't be necessary, as we merge the entry
    // params into the user-provided params in
    // `formatEntry`/`cleanLedgerEntryVar`, but it's a good sanity
    // check and would let us do better validation.
    const fullParameterizedAccount = accountPathToAccount.get(structuralPath);
    const account =
      // TODO: this makes Steven and Jerry very sad, lets fix it later.
      fullParameterizedAccount &&
      reparameterizeSchemaLedgerAccountInput({
        account: fullParameterizedAccount,
        entryParameters,
      });

    // This is a truly horrendous hack. An unresolved account is already a zod
    // issue, so the schema will fail to render — but the line is still built (zod
    // needs to collect all of the errors), causing typescript to think Schema
    // allows for lines w/undefined currency. This basically coerces to something
    // random to avoid that issue.
    const currency = parameterizedLine.currency ||
      account?.currency || { code: 'USD' };

    return {
      ...parameterizedLine,
      parameters: parameterizedLine.parameters.concat(
        account ? account.parameters! : []
      ),
      // INC-62: We need to use the user-provided path for the account.
      //         Do not assign it to account.path as it's a shared object
      //         across entries.
      account: {
        ...account,
        path: accountPath,
      },
      accountPath,
      currency,
    };
  });

export const SchemaInt96ConditionInput = z.object({
  eq: ParameterizedString.optional(),
  lte: ParameterizedString.optional(),
  gte: ParameterizedString.optional(),
});

export const SchemaConditionInput = z.object({
  ownBalance: SchemaInt96ConditionInput.optional(),
  totalBalance: SchemaInt96ConditionInput.optional(),
});

export const SchemaLedgerEntryConditionInput = z.object({
  account: z.object({
    path: ParameterizedString,
  }),
  postcondition: SchemaConditionInput.optional(),
  precondition: SchemaConditionInput.optional(),
  currency: SchemaCurrencyMatchInput.optional(),
  repeated: z
    .object({
      key: SafeStringSchema.refine((k) => k.trim().length >= 1, {
        message: 'repeated.key cannot be empty',
      }),
    })
    .optional(),
});

type SchemaLedgerEntryConditionInputType = z.infer<
  typeof SchemaLedgerEntryConditionInput
>;
type ParameterizedSchemaLedgerEntryConditionInputType<
  T extends SchemaLedgerEntryConditionInputType
> = T & {
  parameters: string[];
};

/**
 * This function converts a schema ledger line input into a parameterized
 * version, filling in any parameters that are available in the entry first.
 */
export const parameterizeSchemaLedgerEntryConditionInput = <
  T extends SchemaLedgerEntryConditionInputType
>({
  condition,
  entryParameters,
  fillParamOptions = {
    allowMissing: true,
  },
}: {
  condition: T;
  entryParameters: Record<string, string> | undefined;
  fillParamOptions?: Parameters<typeof fillParams>[2];
}): ParameterizedSchemaLedgerEntryConditionInputType<T> => {
  // console.log(
  //   '[parameterizeSchemaLedgerEntryConditionInput]',
  //   entryParameters,
  //   fillParamOptions
  // );
  let filledCondition = condition;
  if (entryParameters) {
    // condition does not include the new account path
    // console.log(
    //   '[parameterizeSchemaLedgerEntryConditionInput] calling fillParams',
    //   condition,
    //   entryParameters,
    //   {
    //     ...fillParamOptions,
    //   }
    // );
    filledCondition = fillParams(condition, entryParameters, {
      ...fillParamOptions,
    });
  }
  return {
    ...filledCondition,
    parameters: getSchemaObjectParameters(filledCondition),
  };
};

const SchemaLedgerEntryTagInput = z.object({
  key: SafeStringSchema.refine((key) => key.trim().length >= 1, {
    message: 'Tag key cannot be blank',
  }).refine((key) => key.length <= 128, {
    message: 'Tag key cannot be longer than 128 characters',
  }),
  value: ParameterizedString,
});

export const SchemaLedgerEntryTagsInput = z
  // deduplication / validation is done later
  .array(SchemaLedgerEntryTagInput)
  .optional();

const SchemaLedgerEntryGroupInput = z.object({
  key: SafeStringSchema.refine((key) => key.trim().length >= 1, {
    message: 'Group key cannot be blank',
  }).refine((key) => key.length <= 128, {
    message: 'Group key cannot be longer than 128 characters',
  }),
  value: ParameterizedString,
});

export const SchemaLedgerEntryGroupsInput = z
  // deduplication / validation is done later
  .array(SchemaLedgerEntryGroupInput)
  .optional();

export const SchemaLedgerEntryStatus = z.enum([
  'active',
  'disabled',
  'archived',
]);

export const SchemaLedgerEntryInput = z
  .object({
    type: SafeStringSchema.refine((s) => s.trim().length > 0),
    typeVersion: z.number().int().positive().optional(),
    description: ParameterizedString.optional(),
    conditions: z.array(SchemaLedgerEntryConditionInput).optional(),
    lines: z.array(SchemaLedgerLineInput).optional(),
    // Since the input type is `JSON` in graphql, we don't know the shape that we'll get.
    // Zod will infer `input` type if we use `z.record` directly, so we need to first use `z.unknown()`
    parameters: z
      .unknown()
      .optional()
      .transform((value) =>
        // We don't want empty keys or values
        z
          .record(
            z.string().min(1, {
              message:
                'Static parameters on ledger entries cannot have empty keys',
            }),
            z.string().min(1, {
              message:
                'Static parameters on ledger entries cannot have empty values',
            })
          )
          .optional()
          .parse(value)
      ),
    tags: SchemaLedgerEntryTagsInput,
    groups: SchemaLedgerEntryGroupsInput,
    status: SchemaLedgerEntryStatus.optional(),
    postLinesAs: PostLinesAsSchema.optional(),
  })
  .refine(
    (entry) => (entry.lines ?? []).filter((line) => line.tx).length <= 2,
    { message: 'At most two lines can have a tx match' } // TODO - move in to entry validation code
  )
  .refine(
    (entry) => {
      const txs = entry.lines?.flatMap((line) =>
        line.tx?.id ? [line.tx.id] : []
      );
      return !txs || new Set(txs).size === txs.length;
    },
    { message: 'Duplicate tx id matches are not allowed' }
  )
  .refine(
    (entry) => {
      const txs = entry.lines?.flatMap((line) =>
        line.tx?.externalId ? [line.tx.externalId] : []
      );
      return !txs || new Set(txs).size === txs.length;
    },
    { message: 'Duplicate tx id matches are not allowed' }
  );

export const SchemaLedgerEntriesInput = z.object({
  types: z.array(SchemaLedgerEntryInput),
});

export type SchemaLedgerEntriesInputType = z.input<
  typeof SchemaLedgerEntriesInput
>;

type SchemaLedgerEntryInputType = z.infer<typeof SchemaLedgerEntryInput>;

/** Enriches a journal entry's lines and fills its conditions' parameters. */
const enrichJournalEntry = ({
  entry,
  accountPathToAccount,
}: {
  entry: SchemaLedgerEntryInputType;
  accountPathToAccount: ChartOfAccountsType['accountPathToAccount'];
}) => ({
  ...entry,
  conditions: entry.conditions?.map((condition) =>
    parameterizeSchemaLedgerEntryConditionInput({
      condition,
      entryParameters: entry.parameters,
    })
  ),
  lines:
    entry.lines &&
    enrichSchemaLines({
      lines: entry.lines,
      entryParameters: entry.parameters,
      accountPathToAccount,
    }),
});

export const SchemaPaymentLineInput = SchemaLedgerLineInput.pick({
  key: true,
  account: true,
  currency: true,
  description: true,
}).extend({
  // Required, unlike ledger lines — payment entries have no runtime-filled
  // variant.
  amount: ParameterizedAmount(),
  system: SystemLineKindSchema.optional(),
});

// A strict subset of the entry-version attributes — description and lines
// only; not an entry type (no `type` string).
export const SchemaPaymentEntryInput = z.object({
  description: ParameterizedString.optional(),
  lines: z.array(SchemaPaymentLineInput),
});

type SchemaPaymentEntryInputType = z.infer<typeof SchemaPaymentEntryInput>;

export const SchemaPaymentTypeInput = z.object({
  type: SafeStringSchema,
  typeVersion: z.number().int().positive(),
  status: SchemaPaymentTypeStatusSchema,
  payment: z.object({
    amount: ParameterizedAmount(),
    direction: PaymentTypeDirectionSchema,
  }),
  accounting: z.object({
    initiated: SchemaPaymentEntryInput.optional(),
    settled: SchemaPaymentEntryInput,
    refund_initiated: SchemaPaymentEntryInput.optional(),
    refund_failed: SchemaPaymentEntryInput.optional(),
  }),
});

type SchemaPaymentTypeInputType = z.infer<typeof SchemaPaymentTypeInput>;

/** Enriches one payment accounting entry's lines. */
const enrichPaymentEntry = ({
  entry,
  accountPathToAccount,
}: {
  entry: SchemaPaymentEntryInputType;
  accountPathToAccount: ChartOfAccountsType['accountPathToAccount'];
}) => ({
  ...entry,
  lines: enrichSchemaLines({
    lines: entry.lines,
    // Payment entries hold no static parameters — every parameter is supplied
    // per payment when the entry posts.
    entryParameters: undefined,
    accountPathToAccount,
  }),
});

/** Enriches every accounting entry a Payment Type declares. */
const enrichPaymentType = ({
  paymentType,
  accountPathToAccount,
}: {
  paymentType: SchemaPaymentTypeInputType;
  accountPathToAccount: ChartOfAccountsType['accountPathToAccount'];
}) => {
  const enrichEntry = (entry: SchemaPaymentEntryInputType) =>
    enrichPaymentEntry({ entry, accountPathToAccount });
  // Every event but the one a Payment Type must declare is mapped over, so a
  // new event is enriched without being named here.
  const { settled, ...optionalEvents } = paymentType.accounting;
  return {
    ...paymentType,
    accounting: {
      ...mapValues(optionalEvents, (entry) => entry && enrichEntry(entry)),
      settled: enrichEntry(settled),
    },
  };
};

export const SchemaPaymentsInput = z.object({
  types: z.array(SchemaPaymentTypeInput).min(1, {
    message:
      'At least one Payment Type is required. Omit `payments` to store a schema without Payment Types.',
  }),
});

export const LedgerConsistencyConfigSchema = ConsistencyConfigSchema(
  ['entries'] as const,
  {}
);

const SceneParameters = z
  .unknown()
  .refine((value) => typeof value === 'object', {
    message: 'Parameters must be an object',
  })
  .transform((value) => z.record(z.string()).parse(value));

const SceneLedgerEntry = z.object({
  type: SafeStringSchema,
  typeVersion: z.optional(z.number().int().positive()),
  parameters: SceneParameters,
});

const ScenePayment = z.object({
  ik: SafeStringSchema,
  type: SafeStringSchema,
  typeVersion: z.optional(z.number().int().positive()),
  parameters: SceneParameters,
});

const ScenePaymentEvent = z.object({
  ik: SafeStringSchema,
  event: PaymentAccountingEventKeySchema,
});

// GraphQL inputs cannot be unions, so the input carries both payloads as
// optional fields; the pipe pairs the discriminant with its payload.
const SceneEvent = z
  .object({
    eventType: z.enum(['entry', 'payment']),
    entry: z.optional(SceneLedgerEntry),
    payment: z.optional(ScenePaymentEvent),
  })
  .pipe(
    z.discriminatedUnion('eventType', [
      z.object({ eventType: z.literal('entry'), entry: SceneLedgerEntry }),
      z.object({ eventType: z.literal('payment'), payment: ScenePaymentEvent }),
    ])
  );

export const Scene = z
  .object({
    name: SafeStringSchema,
    events: z.array(SceneEvent),
    payments: z.optional(z.array(ScenePayment)),
  })
  .superRefine((scene, ctx) => {
    const declaredIks = new Set<string>();
    (scene.payments ?? []).forEach((payment, index) => {
      if (declaredIks.has(payment.ik)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['payments', index, 'ik'],
          message: `Duplicate simulated payment ik '${payment.ik}' in Scene '${scene.name}'.`,
        });
      }
      declaredIks.add(payment.ik);
    });

    scene.events.forEach((event, index) => {
      if (event.eventType !== 'payment') return;
      if (declaredIks.has(event.payment.ik)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['events', index, 'payment', 'ik'],
        message: `Scene '${scene.name}' has an event for simulated payment '${event.payment.ik}', which it does not declare in \`payments\`.`,
      });
    });
  });

export type SceneType = z.TypeOf<typeof Scene>;

export const Schema = z
  .object({
    name: z.string().optional(),
    key: SafeStringSchema.refine(
      (s) => s.trim().length > 0 && !s.includes('{{') && !s.includes('}}'),
      {
        message: `Invalid key. Keys cannot contain '{{' or '}}' and must be non-empty`,
      }
    ),
    chartOfAccounts: ChartOfAccounts,
    ledgerEntries: SchemaLedgerEntriesInput.optional(),
    payments: SchemaPaymentsInput.optional(),
    consistencyConfig: z.optional(LedgerConsistencyConfigSchema),
    scenes: z.array(Scene).optional(),
    groups: z.array(SchemaEntryGroup).optional(),
  })
  .transform((schema, ctx) => {
    const {
      chartOfAccounts,
      ledgerEntries,
      consistencyConfig,
      groups,
      payments,
    } = schema;

    const { errors, filledIn: validatedLedgerEntries } =
      transformAndValidateEntries({
        ledgerEntries: ledgerEntries ?? { types: [] },
        validatedCoA: chartOfAccounts,
      });
    errors
      .map((e) => ({ ...e, path: ['ledgerEntries', ...e.path] }))
      .forEach(({ message, path }) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path,
        })
      );

    transformAndValidatePayments({
      payments: payments ?? { types: [] },
      validatedCoA: chartOfAccounts,
    })
      .errors.map((e) => ({ ...e, path: ['payments', ...e.path] }))
      .forEach(({ message, path }) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path,
        })
      );

    validateReservedPaymentEntryTypes({
      ledgerEntryTypes: ledgerEntries?.types ?? [],
      paymentTypes: payments?.types ?? [],
    })
      .map((e) => ({ ...e, path: ['ledgerEntries', ...e.path] }))
      .forEach(({ message, path }) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path,
        })
      );

    // Validate flows if they exist
    if (groups && groups.length > 0) {
      groups.forEach((group, index) => {
        const groupErrors = validateGroup(
          group,
          chartOfAccounts,
          validatedLedgerEntries,
          index
        );
        groupErrors.forEach(({ message, path }) =>
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path,
          })
        );
      });
    }

    const { accountPathToAccount } = chartOfAccounts;
    const ledgerEntryTypes = (ledgerEntries?.types ?? []).map((entry) =>
      enrichJournalEntry({ entry, accountPathToAccount })
    );
    const enrichedPayments = payments && {
      ...payments,
      types: payments.types.map((paymentType) =>
        enrichPaymentType({ paymentType, accountPathToAccount })
      ),
    };

    return {
      ...schema,
      ledgerEntries: { types: ledgerEntryTypes },
      payments: enrichedPayments,
      consistencyConfig: consistencyConfig ?? {
        entries: 'eventual',
      },
    };
  });

export type SchemaUserInputType = z.input<typeof Schema>;

export type SchemaInputType = z.infer<typeof Schema>;
export type LedgerEntriesType = z.infer<typeof Schema>['ledgerEntries'];
export type LedgerEntrySchemaType = LedgerEntriesType['types'][number];
export type PaymentsType = z.infer<typeof Schema>['payments'];
export type ConsistencyConfigType = z.infer<typeof Schema>['consistencyConfig'];
export type SchemaLedgerEntryCondition = z.infer<
  typeof SchemaLedgerEntryConditionInput
>;
export type TypedLedgerEntry = z.infer<
  typeof Schema
>['ledgerEntries']['types'][0];
export type TypedLedgerEntryLine = NonNullable<TypedLedgerEntry['lines']>[0];
/* Type of the Parsed Ledger Account after the defaults are filled in */
export type ParsedLedgerAccountType = ChartOfAccountsType['accounts'][number];
